import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { getQuotaAddingQuarters, createCompanyQuotaThunk, createCompanyQuotaOverrideThunk, getCurrentAndFutureCompanyQuotasThunk } from '../redux/management.thunk';
import { selectIsCreateSuccess, selectQuotaAddingQuarters, selectCurrentQuotaData, clearCurrentQuotaData } from '../redux/management.slice';
import QuotaAssignmentSection from './QuotaAssignmentSection';
import ConflictDialog from './ConflictDialog';

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalPaper: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: '8px',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(4),
    width: '950px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(3),
  },
  closeButton: {
    marginTop: -theme.spacing(1),
    marginRight: -theme.spacing(1),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  dialogActions: {
    justifyContent: 'center',
    paddingBottom: theme.spacing(2),
  }
}));

const CompanyQuotaEditorModal = ({ open, onClose, existingQuotas, selectedQuota }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const currencySymbol = useSelector(state => state.app.user.Currency);
  const quotaAddingQuarters = useSelector(selectQuotaAddingQuarters);
  const isCreateSuccess = useSelector(selectIsCreateSuccess);
  const currentQuotaData = useSelector(selectCurrentQuotaData);

  const [quotaAssignments, setQuotaAssignments] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflictData, setConflictData] = useState([]);

  const allAvailableQuartersSorted = useMemo(() => {
    if (!quotaAddingQuarters) return [];
    const quarters = [
      ...(quotaAddingQuarters?.previousQuarters || []),
      quotaAddingQuarters?.currentQuarter,
      ...(quotaAddingQuarters?.futureQuarters || [])
    ].filter(Boolean);
    const uniqueQuarters = [...new Set(quarters)];
    return uniqueQuarters.sort((a, b) => {
      const [yearA, quarterA] = a.split('-Q').map(Number);
      const [yearB, quarterB] = b.split('-Q').map(Number);
      if (yearA !== yearB) return yearA - yearB;
      return quarterA - quarterB;
    });
  }, [quotaAddingQuarters]);

  useEffect(() => {
    if (open && !quotaAddingQuarters) {
      dispatch(getQuotaAddingQuarters({ type: 'company' }));
    }
  }, [open, dispatch, quotaAddingQuarters]);

  useEffect(() => {
    if (open && quotaAddingQuarters && allAvailableQuartersSorted.length > 0 && quotaAssignments.length === 0) {
      // Always show current quarter + 2 future quarters layout
      const { currentQuarter } = quotaAddingQuarters;
      const currentIndex = allAvailableQuartersSorted.indexOf(currentQuarter);

      if (currentIndex >= 0) {
        const initialQuarters = allAvailableQuartersSorted.slice(currentIndex, currentIndex + 3);

        // Pre-populate with existing data if available
        const defaultQuarters = initialQuarters.map((quarter, index) => {
          // Find existing data for this quarter
          const existingData = existingQuotas?.find(q => q.Fiscal_Quarter === quarter);

          return {
            id: Date.now() + Math.random() + index,
            fiscalQuarter: quarter,
            quota: existingData?.Quota || '',
            nqpGoal: existingData?.NQP_GOAL || '',
          };
        });

        setQuotaAssignments(defaultQuarters);
      }
    }
  }, [open, quotaAddingQuarters, allAvailableQuartersSorted, quotaAssignments.length, existingQuotas]);

  useEffect(() => {
    if (isCreateSuccess) {
      handleClose();
      // Refresh the company quotas list
      dispatch(getCurrentAndFutureCompanyQuotasThunk());
    }
  }, [isCreateSuccess, dispatch]);

  // Handle conflict validation errors
  useEffect(() => {
    if (currentQuotaData && Array.isArray(currentQuotaData)) {
      const conflicts = currentQuotaData.filter(item => item.canUpsert === false);
      if (conflicts.length > 0) {
        setConflictData(currentQuotaData);
        setConflictDialogOpen(true);
      }
    }
  }, [currentQuotaData]);

  const handleClose = useCallback(() => {
    setQuotaAssignments([]);
    setIsSaving(false);
    setConfirmOpen(false);
    setConflictDialogOpen(false);
    setConflictData([]);
    dispatch(clearCurrentQuotaData());
    onClose();
  }, [onClose, dispatch]);

  const canAddPreviousQuarter = useMemo(() => {
    if (quotaAssignments.length === 0 || allAvailableQuartersSorted.length === 0) return false;
    const firstQuarter = quotaAssignments[0].fiscalQuarter;
    const firstAvailableQuarter = allAvailableQuartersSorted[0];
    return firstQuarter !== firstAvailableQuarter;
  }, [quotaAssignments, allAvailableQuartersSorted]);

  const canAddNextQuarter = useMemo(() => {
    if (allAvailableQuartersSorted.length === 0) return false;
    if (quotaAssignments.length === 0) return true;
    const lastQuarter = quotaAssignments[quotaAssignments.length - 1].fiscalQuarter;
    const lastAvailableQuarter = allAvailableQuartersSorted[allAvailableQuartersSorted.length - 1];
    return lastQuarter !== lastAvailableQuarter;
  }, [quotaAssignments, allAvailableQuartersSorted]);

  const handleAddPreviousQuarter = () => {
    if (quotaAssignments.length === 0 || allAvailableQuartersSorted.length === 0) return;
    const firstQuarter = quotaAssignments[0].fiscalQuarter;
    const currentIndex = allAvailableQuartersSorted.indexOf(firstQuarter);
    if (currentIndex > 0) {
      const previousQuarter = allAvailableQuartersSorted[currentIndex - 1];
      const newAssignment = {
        id: Date.now() + Math.random(),
        fiscalQuarter: previousQuarter,
        quota: '',
        nqpGoal: '',
      };
      setQuotaAssignments([newAssignment, ...quotaAssignments]);
    }
  };

  const handleAddNextQuarter = () => {
    if (allAvailableQuartersSorted.length === 0) return;
    if (quotaAssignments.length === 0) {
      if (quotaAddingQuarters?.currentQuarter) {
        const newAssignment = {
          id: Date.now() + Math.random(),
          fiscalQuarter: quotaAddingQuarters.currentQuarter,
          quota: '',
          nqpGoal: '',
        };
        setQuotaAssignments([newAssignment]);
      }
      return;
    }
    const lastQuarter = quotaAssignments[quotaAssignments.length - 1].fiscalQuarter;
    const currentIndex = allAvailableQuartersSorted.indexOf(lastQuarter);
    if (currentIndex >= 0 && currentIndex < allAvailableQuartersSorted.length - 1) {
      const nextQuarter = allAvailableQuartersSorted[currentIndex + 1];
      const newAssignment = {
        id: Date.now() + Math.random(),
        fiscalQuarter: nextQuarter,
        quota: '',
        nqpGoal: '',
      };
      setQuotaAssignments([...quotaAssignments, newAssignment]);
    }
  };

  const handleQuotaChange = (id, field, value) => {
    setQuotaAssignments(prev =>
      prev.map(a => (a.id === id ? { ...a, [field]: value } : a))
    );
  };

  const handleRemoveQuota = (id) => {
    setQuotaAssignments(prev => prev.filter(a => a.id !== id));
  };

  const isValidRecord = (assignment) => {
    const hasQuota = assignment.quota && String(assignment.quota).trim() !== '';
    const hasNqpGoal = assignment.nqpGoal && String(assignment.nqpGoal).trim() !== '';
    return hasQuota || hasNqpGoal;
  };

  const hasAtLeastOneValidAssignment = useMemo(() => {
    return quotaAssignments.some(isValidRecord);
  }, [quotaAssignments]);

  const handleSave = () => {
    setConfirmOpen(true);
  };

  const handleConfirmSave = () => {
    setConfirmOpen(false);
    setIsSaving(true);

    const validAssignments = quotaAssignments.filter(isValidRecord);
    setQuotaAssignments(validAssignments);

    const payload = validAssignments.map(it => ({
      NQP_Goal: it.nqpGoal ? parseInt(it.nqpGoal) : null,
      quota: it.quota ? parseInt(it.quota) : null,
      fiscalQuarter: it.fiscalQuarter,
    }));

    const createCompanyQuotaBody = {
      quotaAssignments: payload,
    };

    dispatch(createCompanyQuotaThunk({ createCompanyQuotaBody }));
    console.log('Dispatching company quota save with:', createCompanyQuotaBody);

    setIsSaving(false);
  };

  const handleConflictDialogClose = () => {
    setConflictDialogOpen(false);
    setConflictData([]);
    dispatch(clearCurrentQuotaData());
  };

  const handleOverwriteConfirm = () => {
    const updatedConflictData = conflictData.map(item => ({
      ...item,
      canUpsert: true
    }));

    const quotaAssignmentsPayload = updatedConflictData.map(item => ({
      NQP_Goal: item.NQP_Goal,
      quota: item.quota,
      fiscalQuarter: item.fiscalQuarter,
      canUpsert: true,
    }));

    const createCompanyQuotaBody = {
      quotaAssignments: quotaAssignmentsPayload,
    };

    dispatch(createCompanyQuotaOverrideThunk({ createCompanyQuotaBody }));
    setConflictDialogOpen(false);
    setConflictData([]);
  };

  return (
    <>
      <Modal open={open} onClose={handleClose} className={classes.modal}>
        <Box className={classes.modalPaper}>
          <div className={classes.modalHeader}>
            <Typography variant="h6">Add Quota: Company</Typography>
            <IconButton className={classes.closeButton} onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </div>

          <QuotaAssignmentSection
            assignments={quotaAssignments}
            onQuotaChange={handleQuotaChange}
            onRemoveQuota={handleRemoveQuota}
            onAddPreviousQuarter={handleAddPreviousQuarter}
            onAddNextQuarter={handleAddNextQuarter}
            canAddPreviousQuarter={canAddPreviousQuarter}
            canAddNextQuarter={canAddNextQuarter}
            allAvailableQuarters={allAvailableQuartersSorted}
            isLoadingQuarters={!quotaAddingQuarters}
            isSaving={isSaving}
            currencySymbol={currencySymbol}
            hideRampStatus={true}
          />

          <div className={classes.actions}>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={isSaving || !hasAtLeastOneValidAssignment}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </Box>
      </Modal>
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">Confirm Add Company Quota</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            Are you sure you want to add quota entries for the company for the selected fiscal quarters?
          </DialogContentText>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={handleConfirmSave} variant="contained" color="primary">
            Yes
          </Button>
          <Button onClick={() => setConfirmOpen(false)} variant="contained" color="primary">
            No
          </Button>
        </DialogActions>
      </Dialog>

      <ConflictDialog
        open={conflictDialogOpen}
        onClose={handleConflictDialogClose}
        onConfirm={handleOverwriteConfirm}
        conflictData={conflictData}
        hideRampStatus={true}
      />
    </>
  );
};

export default CompanyQuotaEditorModal;
